import React, { useCallback, useEffect, useState } from 'react';
import { checkoutCountryList as countryList } from 'shared/data/siteSettings';
import Dropdown from 'components/commercetools-ui/atoms/dropdown';
import Input from 'components/commercetools-ui/atoms/input';
import useLocale from 'helpers/hooks/useLocale';
import AfdComponent from 'frontastic/tastics/address-afd';
import { Fields, FieldsOptions } from './types';
import { Address } from '../../types';

interface Props {
  className?: string;
  address: Address;
  addressType: string;
  fields: (options: FieldsOptions) => Fields[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit?: () => void;
  onAFDAddressSelection?: (addressInfo: AddressInfo) => void;
  onAddressCountrySelection?: (countryCode: string, addressType: string) => void;
  onSelectedTitle?: ((selectedTitle: string, addressType: string) => void) | ((selectedTitle: string) => void);
  restrictEmailField?: boolean;
}
interface AddressInfo {
  addressType?: string;
  line1?: string;
  line2?: string;
  postcode?: string;
  town?: string;
  postalCounty?: string;
}

const AddressForm = ({
  className: containerClassName,
  fields,
  address,
  addressType,
  onChange,
  onSubmit,
  onAFDAddressSelection,
  onAddressCountrySelection,
  onSelectedTitle,
  restrictEmailField = false,
  children,
}: React.PropsWithChildren<Props>) => {
  const { getCountry } = useLocale();
  const [enableAddress2, setEnableAddress2] = useState(false);
  const [addressCountryCode, setcountryCode] = useState(getCountry());
  const [selectedTitle, setSelectedTitle] = useState('');

  const onEnableAddress2 = useCallback(() => setEnableAddress2(true), []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit?.();
    },
    [onSubmit],
  );

  const getAddressDetails = (addressInfo: AddressInfo) => {
    // Handle the data received from the child component
    addressInfo.addressType = addressType;
    addressInfo.line2 ? setEnableAddress2(true) : setEnableAddress2(false);
    if (onAFDAddressSelection) {
      onAFDAddressSelection(addressInfo);
    } else {
      console.log('onAFDAddressSelection is not passed');
    }
  };

  const dropdownList =
    countryList.length > 0
      ? [...countryList.map(({ countryCode, country }) => ({ label: country, value: countryCode }))]
      : [];

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setcountryCode(e.target.value);
    if (onAddressCountrySelection) {
      onAddressCountrySelection(e.target.value, addressType);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTitle(e.target.value);
    onSelectedTitle?.(e.target.value, addressType);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={`grid grid-cols-4 gap-20 ${containerClassName}`}>
        <div className="col-span-4">
          <Dropdown
            name="title"
            value={address['title' as keyof Address] as string}
            items={[
              { label: 'Mr', value: 'Mr' },
              { label: 'Mrs', value: 'Mrs' },
              { label: 'Ms', value: 'Ms' },
              { label: 'Miss', value: 'Miss' },
              { label: 'Dr', value: 'Dr' },
            ]}
            onChange={handleTitleChange}
            className="w-full border-neutral-500 text-16 font-light"
            label="Title"
            isRequired={true}
          />
        </div>
        {fields({ enableAddress2, onEnableAddress2 }).map(
          ({ name, label, labelDesc, type, required, className, maxLength, render, validate }) => (
            <React.Fragment key={name}>
              {name == 'email' && restrictEmailField ? (
                <></>
              ) : (
                <div className={className + ' ' + className}>
                  <Input
                    name={name}
                    label={label}
                    labelDesc={labelDesc}
                    type={type}
                    required={required}
                    value={address[name as keyof Address] as string | number | readonly string[] | undefined}
                    maxLength={maxLength}
                    labelPosition="top"
                    isValid={
                      (!required || (required && !!address[name as keyof Address])) &&
                      (validate
                        ? name == 'postalCode'
                          ? validate(
                              address[name as keyof Address] as string,
                              address['country' as keyof Address] as string,
                            )
                          : validate(address[name as keyof Address] as string)
                        : true)
                    }
                    onChange={onChange}
                    hideCheckIcon
                    variant="primary"
                  />
                  {render?.()}
                </div>
              )}
              {name == 'phone' && (
                <div className="col-span-4">
                  <Dropdown
                    name="selectCountry"
                    value={addressCountryCode}
                    defaultValue={getCountry()}
                    items={dropdownList}
                    onChange={handleChange}
                    className="w-full border-neutral-500 text-16 font-light"
                    label="Country"
                    isRequired={true}
                  />
                </div>
              )}
              {name == 'phone' && addressCountryCode == 'GB' && (
                <>
                  <AfdComponent callToParent={getAddressDetails} />
                </>
              )}
            </React.Fragment>
          ),
        )}
      </div>
      {children}
    </form>
  );
};

export default AddressForm;
